// Pure, framework-free navigation helpers for shared Rithmo sign-in + the MCP OAuth consent detour (plan §Stage 3, T02/T03).
// Kept side-effect-free (only URLSearchParams) so the round-trip logic is unit-testable in node without jsdom/react-router.

const SSO_CODE_PARAM = 'sso_code'

// localStorage key for carrying the MCP OAuth consent context across a login → verify-email → invite-accept detour. Mirrors
// how the invite token is preserved (constants/orgInvite.PENDING_ORG_INVITE_TOKEN_KEY).
export const PENDING_OAUTH_CONTEXT_KEY = 'rithmo_pending_oauth_context'

// The OAuth authorize params ChatGPT / claude.ai / Claude Code hand to the consent route, PLUS the WorkOS external_auth_id
// that the backend /mcp/oauth/sign-in redirect carries into /mcp-authorize. Presence of ANY of these marks a request that
// must be round-tripped intact through sign-in (so a login/verify/invite detour never drops the authorization).
const OAUTH_CONTEXT_KEYS = [
  'external_auth_id',
  'client_id',
  'redirect_uri',
  'response_type',
  'state',
  'scope',
  'code_challenge',
  'code_challenge_method',
  'resource',
  'nonce',
  // Routing hints (R4): which company/connection this authorization is for. They MUST survive the login/verify/invite detours so
  // the consent route selects the right connection on return (not just the first eligible one).
  'sso_org',
  'connection',
]

/** Extract a one-time SSO completion code from a location search string (the ?sso_code= callback redirect). Null if absent. */
export function extractSsoCode(search) {
  const code = new URLSearchParams(search || '').get(SSO_CODE_PARAM)
  return code ? code : null
}

/** The search string (leading '?' included, or '' when empty) with sso_code removed — used to scrub the one-time code from the URL. */
export function stripSsoCode(search) {
  const params = new URLSearchParams(search || '')
  params.delete(SSO_CODE_PARAM)
  const s = params.toString()
  return s ? `?${s}` : ''
}

/**
 * Build the backend SSO-start URL (a full-page navigation, NOT a fetch — the browser follows the redirect to the IdP).
 * @param {{ apiBase?: string, org: string, returnTo?: string }} args
 */
export function buildSsoStartUrl({ apiBase = '', org, returnTo } = {}) {
  if (!org) throw new Error('org is required to start SSO')
  const base = apiBase.replace(/\/+$/, '')
  const params = new URLSearchParams({ org })
  if (returnTo) params.set('return_to', returnTo)
  return `${base}/auth/sso/start?${params.toString()}`
}

/**
 * Capture the OAuth consent context from a location search string as a compact query string, so it survives a sign-in
 * detour. Returns '' when there is nothing meaningful to preserve (so callers can treat '' as "no OAuth context").
 */
export function captureOAuthContext(search) {
  const params = new URLSearchParams(search || '')
  if (!OAUTH_CONTEXT_KEYS.some((k) => params.has(k))) return ''
  const kept = new URLSearchParams()
  for (const k of OAUTH_CONTEXT_KEYS) {
    const v = params.get(k)
    if (v != null) kept.set(k, v)
  }
  return kept.toString()
}

/** Rebuild the consent-route path (/mcp-authorize?<context>) from a preserved context string; null when there is none. */
export function resumeOAuthContextPath(preserved, { route = '/mcp-authorize' } = {}) {
  if (!preserved) return null
  return `${route}?${preserved}`
}

/**
 * A BACKEND-SAFE consent resume path for an SSO round-trip (the `return_to` handed to /api/auth/sso/start). The backend's
 * safeReturnTo rejects any path whose query carries a scheme (e.g. redirect_uri=https:…), so the FULL context path from
 * resumeOAuthContextPath would be dropped to the fallback. We carry ONLY the id-like keys the consent route consumes —
 * external_auth_id (required) and the connection routing hint — which is sufficient for Rithmo to complete the authorization
 * (WorkOS holds the original redirect_uri/PKCE/state bound to external_auth_id). Returns '' when there is no external_auth_id.
 */
export function consentResumeReturnTo(preserved, { route = '/mcp-authorize' } = {}) {
  if (!preserved) return ''
  const p = new URLSearchParams(preserved)
  const eaid = p.get('external_auth_id')
  if (!eaid) return ''
  const out = new URLSearchParams({ external_auth_id: eaid })
  const conn = p.get('connection')
  if (conn) out.set('connection', conn)
  return `${route}?${out.toString()}`
}

/**
 * Build the /login redirect used when the consent route bounces a signed-out user to sign in. Preserves the `sso_org` routing
 * hint so LoginPage still shows the company-SSO entry (it only renders when sso_org is present). The OAuth authorization
 * handle is carried separately in the bounded, session-scoped continuation (context/oauthContinuation.js), NOT on this URL
 * (so analytics on /login never sees it).
 */
export function buildLoginRedirect(search) {
  const org = new URLSearchParams(search || '').get('sso_org')
  return org ? `/login?sso_org=${encodeURIComponent(org)}` : '/login'
}

/** Parse the MCP consent-route context (/mcp-authorize?external_auth_id=…). Returns the externalAuthId and the full search. */
export function parseConsentContext(search) {
  const params = new URLSearchParams(search || '')
  return { externalAuthId: params.get('external_auth_id') || '' }
}

/** Default connection selection for the consent UI: the first eligible connection, or null when the list is empty. */
export function selectDefaultConnection(connections) {
  if (!Array.isArray(connections) || connections.length === 0) return null
  return connections[0]?.connectionId ?? null
}

/**
 * Select the connection for the consent UI honoring the carried routing context (R4): if the authorization named a `connection`
 * AND it is in the server-returned ELIGIBLE list, use it; otherwise fall back to the first eligible connection. The choice is
 * always constrained to the eligible list (server-side eligibility is authoritative — the client never invents a connection).
 */
export function selectRoutedConnection(connections, search) {
  if (!Array.isArray(connections) || connections.length === 0) return null
  const wanted = new URLSearchParams(search || '').get('connection')
  if (wanted && connections.some((c) => c?.connectionId === wanted)) return wanted
  return selectDefaultConnection(connections)
}

/**
 * Decide where to go after a successful sign-in, honoring the verify-email and invite detours AND any preserved OAuth
 * consent context. Pure: returns a { path } the caller navigates to (plus whether it is a replace). Ordering:
 *   1. not email-verified → /verify-email (the bounded continuation is kept by the caller; oauthContinuation.js)
 *   2. invite token → /join?token=... (accepting the invite establishes the membership a pending OAuth consent may REQUIRE;
 *      the OAuth context stays stashed and JoinPage resumes it after acceptance)
 *   3. pending OAuth consent context → resume /mcp-authorize?<context>
 *   4. otherwise → `from`
 */
export function postSignInDestination({ emailVerified = true, pendingOAuthContext = '', inviteToken = '', from = '/dashboard' } = {}) {
  if (emailVerified === false) return { path: '/verify-email' }
  if (inviteToken) return { path: `/join?token=${encodeURIComponent(inviteToken)}` }
  const resumed = resumeOAuthContextPath(pendingOAuthContext)
  if (resumed) return { path: resumed }
  return { path: from || '/dashboard' }
}
